import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Printer, ArrowLeft, Loader2 } from "lucide-react";
import { brand, fabricDetails, caseStudies } from "../mock";
import html2pdf from "html2pdf.js";

const FabricPlaybook = () => {
  const fabricCases = caseStudies.filter((c) => c.tag === "MICROSOFT FABRIC");
  const contentRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    if (!contentRef.current) return;
    setDownloading(true);
    try {
      // Force light theme for a clean PDF
      const wasDark = document.documentElement.classList.contains("dark");
      if (wasDark) document.documentElement.classList.remove("dark");

      const opt = {
        margin: [10, 10, 10, 10],
        filename: `V4-Microsoft-Fabric-Playbook-${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"], avoid: [".break-inside-avoid"] },
      };
      await html2pdf().set(opt).from(contentRef.current).save();
      if (wasDark) document.documentElement.classList.add("dark");
    } catch (e) {
      window.print();
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-[#F5F7FB] text-[#0B1B3B] min-h-screen playbook-root">
      {/* Toolbar (hidden on print) */}
      <div className="no-print sticky top-0 z-[60] bg-white border-b border-slate-200">
        <div className="container-x flex flex-wrap items-center justify-between gap-3 py-3">
          <Link to="/insights" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B1B3B] hover:text-[#1E56E3]">
            <ArrowLeft size={14} /> Back to insights
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-slate-200 hover:border-[#1E56E3] hover:text-[#1E56E3]">
              <Printer size={14} /> Print
            </button>
            <button onClick={downloadPdf} disabled={downloading} className="btn-primary disabled:opacity-70">
              {downloading ? (<><Loader2 size={14} className="animate-spin" /> Preparing PDF…</>) : (<><Download size={14} /> Download PDF</>)}
            </button>
          </div>
        </div>
      </div>

      {/* Playbook A4 page */}
      <div className="container-x py-6 md:py-10">
        <div ref={contentRef} className="mx-auto w-full max-w-[880px] bg-white border border-slate-200 rounded-xl shadow-sm p-6 md:p-10 print:p-6 print:border-0 print:shadow-none" style={{ color: "#0B1B3B" }}>
          {/* Header */}
          <div className="flex flex-col-reverse sm:flex-row items-start sm:items-center sm:justify-between gap-4 border-b border-slate-200 pb-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#1E56E3] font-extrabold">One-Pager Playbook</div>
              <h1 className="mt-2 text-2xl md:text-3xl font-extrabold leading-tight">Microsoft Fabric — The V4 Data And AI Solutions Playbook</h1>
              <p className="mt-2 text-sm text-slate-600 max-w-xl leading-relaxed">A share-with-your-team summary of how V4 Data And AI Solutions architects and optimizes Microsoft Fabric platforms for enterprise scale.</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg overflow-hidden bg-white border border-slate-200 flex items-center justify-center shrink-0">
                <img src={brand.logoUrl} alt="V4" className="w-full h-full object-contain" crossOrigin="anonymous" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">V4 Data And AI Solutions</div>
                <a href={`mailto:${brand.email}`} className="text-[11px] text-[#1E56E3] font-semibold break-all">{brand.email}</a>
              </div>
            </div>
          </div>

          {/* Why Fabric */}
          <section className="mt-6">
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-[#1E56E3]">Why Microsoft Fabric</h2>
            <p className="mt-2 text-[13px] leading-relaxed text-slate-700">
              Microsoft Fabric consolidates Data Factory, Synapse Data Warehouse, Data Engineering, Real-Time Intelligence, Data Science and Power BI into a single SaaS on top of OneLake — collapsing six vendors into one governed platform with Purview + workspace RBAC + row/column-level security.
            </p>
          </section>

          {/* Pillars */}
          <section className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { t: "OneLake Lakehouse", d: "Bronze / Silver / Gold Medallion with Delta and adaptive Liquid Clustering." },
              { t: "Fabric Warehouse", d: "T-SQL warehouse with dbt-fabric transformations and Direct Lake to Power BI." },
              { t: "Real-Time Intelligence", d: "Event Streams + Eventhouse (KQL) + Reflex for sub-5s operational analytics." },
            ].map((p) => (
              <div key={p.t} className="rounded-lg border border-slate-200 p-4 break-inside-avoid">
                <div className="text-[11px] font-extrabold uppercase tracking-widest text-[#1E56E3]">Pillar</div>
                <div className="mt-1 text-[13px] font-bold">{p.t}</div>
                <p className="mt-1 text-[12px] text-slate-600 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </section>

          {/* Deep-dives */}
          <section className="mt-8">
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-[#1E56E3]">Deep-Dives</h2>
            <div className="mt-3 space-y-6">
              {fabricCases.map((c) => {
                const deep = fabricDetails[c.slug];
                return (
                  <div key={c.slug} className="rounded-lg border border-slate-200 p-4 md:p-5 break-inside-avoid">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-[15px] md:text-base font-extrabold leading-snug">{c.title}</h3>
                        <div className="mt-1 text-[11px] text-slate-500">{c.industry} · {c.duration}</div>
                      </div>
                      <div className="shrink-0 sm:text-right">
                        <div className="text-2xl md:text-3xl font-extrabold text-[#1E56E3] leading-none">{c.metric}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{c.metricLabel}</div>
                      </div>
                    </div>

                    {deep && (
                      <div className="mt-4">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Reference Architecture</div>
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {deep.architecture.layers.map((l, i) => (
                            <React.Fragment key={l.name}>
                              <span className="text-[10px] font-bold px-2 py-1 rounded border border-slate-200 whitespace-nowrap" style={{ color: l.color }}>{l.name}: {l.items[0]}</span>
                              {i < deep.architecture.layers.length - 1 && <span className="text-slate-400 text-xs">→</span>}
                            </React.Fragment>
                          ))}
                        </div>

                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="rounded border border-slate-200 p-3 bg-[#FDF4F4]">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-[#B91C1C]">Before</div>
                            <ul className="mt-2 space-y-1">
                              {deep.before.metrics.map((m) => (
                                <li key={m.k} className="text-[11px] text-slate-700 flex items-start justify-between gap-2">
                                  <span className="flex-1">{m.k}</span>
                                  <b className="text-[#B91C1C] whitespace-nowrap">{m.v}</b>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="rounded border border-slate-200 p-3 bg-[#F0FDF4]">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">After</div>
                            <ul className="mt-2 space-y-1">
                              {deep.after.metrics.map((m) => (
                                <li key={m.k} className="text-[11px] text-slate-700 flex items-start justify-between gap-2">
                                  <span className="flex-1">{m.k}</span>
                                  <b className="text-emerald-700 whitespace-nowrap">{m.v}</b>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Footer */}
          <div className="mt-8 pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-[10px] text-slate-500">
            <div>© {new Date().getFullYear()} V4 Data And AI Solutions</div>
            <div>Talk to us: <a className="text-[#1E56E3] font-semibold" href={`mailto:${brand.email}`}>{brand.email}</a></div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .playbook-root { padding: 0 !important; background: white !important; }
        }
      `}</style>
    </div>
  );
};

export default FabricPlaybook;
